import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ChangeDetectorRef,
    OnDestroy,
} from '@angular/core';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import {
    IChecklist2UnitPeriod,
    IChecklist2UnitWithPeriodModel,
    IChecklistTimeInterval,
} from '@fi-shared/checklist/checklist-v2';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiChecklistViewerSnackbarColorClass } from '@fi-shared/checklist/fi-checklist2-viewer/disabled-viewer-warning-snackbar/disabled-viewer-warning-snackbar.component';
import { Subject } from 'rxjs';

interface IWarningSnackbarDataset {
    iconClass: string;
    textMessageLabel: string;
    snackbarColorClass: FiChecklistViewerSnackbarColorClass;
    actionMessageLabel: string;
}

@Component({
    selector: 'fi-checklist2-audit-on-demand-unit-sharing-settings',
    templateUrl: './audit-on-demand-unit-sharing-settings.component.html',
    styleUrls: ['./audit-on-demand-unit-sharing-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-audit-on-demand-unit-sharing-settings' },
    standalone: false,
})
export class FiChecklist2AuditOnDemandUnitSharingSettingsComponent implements OnInit, OnDestroy {
    @Input() checklistUnitsSchedulePeriods: IChecklist2UnitWithPeriodModel[] = [];
    @Input() timeInterval: IChecklistTimeInterval;
    @Input() isAudit: boolean;
    @Output() createPeriod = new EventEmitter<number>();

    selectedBusinessUnitId: number;
    isPeriodEnabled: boolean = false;
    isEnableIntervalStep: boolean = false;
    periodStartEndDateUpdateInterval: NodeJS.Timeout;

    currentWarningSnackbarDataset: IWarningSnackbarDataset;

    ngOnInit() {
        this.currentWarningSnackbarDataset = this.isAudit
            ? this.auditWarningSnackbarDataset
            : this.onDemandWaringSnackbarDataset;
        if (!this.isAudit) {
            // don't show toggle button for on demand checklists
            this.onPeriodEnabledStatusChange(true);
        }
        this.periodStartEndDateUpdateInterval = setInterval(() => {
            this.cdr.markForCheck(); // for proper period start/end date time updating
        }, 5000);
    }

    constructor(
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
        private fiChecklist2ScheduleIntervalService: FiChecklist2ScheduleIntervalService,
        private fiChecklist2ScheduleFormatService: FiChecklist2ScheduleFormatService,
        private cdr: ChangeDetectorRef,
    ) {}

    toggleEnableIntervalStep() {
        this.isEnableIntervalStep = !this.isEnableIntervalStep;
        if (!this.isEnableIntervalStep) {
            if (this.isAudit) {
                this.isPeriodEnabled = false;
            }
            this.selectedBusinessUnitId = undefined;
        }
    }

    onPeriodEnabledStatusChange(isEnabled: boolean) {
        this.isPeriodEnabled = isEnabled;
    }

    private getPeriodStartDateTime(): Date {
        return new Date();
    }

    private getPeriodEndDateTime(): Date {
        const startDateTime = this.getPeriodStartDateTime();
        return new Date(
            startDateTime.getTime() +
                this.fiChecklist2ScheduleIntervalService.convertIntervalDataToMs(this.timeInterval.End),
        );
    }

    saveUnitPeriod() {
        this.createPeriod.emit(this.selectedBusinessUnitId);
    }

    isCurrentUnitPeriodSet(): boolean {
        return this.isPeriodEnabled;
    }

    getToggleValueLabel(): string {
        return this.isPeriodEnabled ? `${this.getPeriodDate(this.getPeriodStartDateTime().toString())}` : '';
    }

    getDueDateLabel(): string {
        return `${this.appTextService.get('checklist.due_date')}: ${this.getDueDatePeriodLabel()}`;
    }

    private getPeriodDate(dateTime: string): string {
        return this.fiFormatDateService.formatDate(new Date(dateTime), FiDateTimeFormatOptions, {
            showToday: false,
            showUTC: true,
        });
    }

    private getDueDatePeriodLabel(): string {
        return this.isCurrentUnitPeriodSet()
            ? this.getPeriodDate(this.getPeriodEndDateTime().toString())
            : this.appTextService.get('common.date_after_start', {
                  '{0}': this.fiChecklist2ScheduleFormatService.getAuditOnDemandEndIntervalLabel(this.timeInterval.End),
              });
    }

    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
    private readonly auditWarningSnackbarDataset: IWarningSnackbarDataset = {
        iconClass: 'far fa-person-chalkboard',
        textMessageLabel: 'checklist.audit_select_unit_and_begin',
        snackbarColorClass: FiChecklistViewerSnackbarColorClass.Error,
        actionMessageLabel: 'common.start',
    } as const;

    private readonly onDemandWaringSnackbarDataset: IWarningSnackbarDataset = {
        iconClass: 'far fa-circle-info',
        textMessageLabel: 'checklist.start_on_demand',
        snackbarColorClass: FiChecklistViewerSnackbarColorClass.Standard,
        actionMessageLabel: 'common.start',
    } as const;

    getChecklistAfterActivationText() {
        return this.appTextService.get(
            !this.isAudit
                ? 'checklist.information_after_activating_toggle'
                : 'checklist.audit_information_after_activating_toggle',
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        clearInterval(this.periodStartEndDateUpdateInterval);
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
